import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Send, Save } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { VoiceDictation } from "../../components/VoiceDictation";
import { DashboardShell } from "../../components/DashboardShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const RATINGS = ["None", "Mild", "Moderate", "Severe"];

const PROMPTS = {
  referral_question: "State the referral question exactly as the evaluation will answer it.",
  sources: "e.g., Clinical interview (date, duration, modality); intake questionnaire; PHQ-9; GAD-7; WHODAS 2.0; records reviewed…",
  procedures: "Describe the evaluation procedures and measures administered.",
  history: "Psychiatric history, treatment, medications, hospitalizations, occupational/educational history relevant to the referral question.",
  behavioral_observations: "Appearance, engagement, speech, cooperation, effort, and notable behaviors during the interview.",
  mse: "Appearance, behavior, speech, mood, affect, thought process/content, insight, judgment, cognition.",
  results: "Auto-drafted from scored measures below — edit as needed.",
  interpretation: "What did the client report? What did the measures show? What did you observe? Integrate — do not restate scores alone.",
  occupational_impact: "Which specific work or academic activities are affected, and how?",
  diagnostic_formulation: "Clinical formulation, including alternative or contributing explanations considered.",
  recommendations: "Clinical recommendations; potential supports/accommodations for consideration by the requesting organization.",
  limitations: "Scope of evaluation, telehealth modality, self-report limitations, and what this evaluation does not determine.",
  interpretive_summary: "Summarize the integrated clinical picture: how history, interview, observations, and measure results converge — and the degree of confidence in the conclusions.",
};

export default function ReportBuilder() {
  const { caseId } = useParams();
  const [report, setReport] = useState(null);
  const [resultsDraft, setResultsDraft] = useState("");
  const [caseDetail, setCaseDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = "You have unsaved changes. Are you sure you want to leave?"; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  useEffect(() => {
    api.get(`/cases/${caseId}/report`).then(async (r) => {
      setReport(r.data);
      if (!r.data.sections?.results) {
        try {
          const d = await api.get(`/clinician/cases/${caseId}`);
          setResultsDraft(d.data.results_summary || "");
        } catch { /* ignore */ }
      }
    }).catch((e) => toast.error(errMsg(e)));
    api.get(`/clinician/cases/${caseId}`).then((r) => setCaseDetail(r.data)).catch(() => {});
  }, [caseId]);

  if (!report) return <DashboardShell title="Report builder"><div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></DashboardShell>;

  const locked = ["qa_review", "approved"].includes(report.status);
  const sections = { ...report.sections };
  if (!sections.results && resultsDraft) sections.results = resultsDraft;
  const matrix = report.functional_matrix || [];
  const att = report.attestation || {};

  const setSection = (k, v) => { setDirty(true); setReport((r) => ({ ...r, sections: { ...sections, [k]: v } })); };
  const setMatrix = (i, k, v) => setReport((r) => {
    setDirty(true);
    const m = [...(r.functional_matrix || [])];
    m[i] = { ...m[i], [k]: v };
    return { ...r, functional_matrix: m };
  });
  const setAtt = (k, v) => { setDirty(true); setReport((r) => ({ ...r, attestation: { ...r.attestation, [k]: v } })); };

  const save = async (quiet) => {
    setSaving(true);
    try {
      await api.put(`/cases/${caseId}/report`, { sections: report.sections, functional_matrix: report.functional_matrix, attestation: report.attestation });
      setDirty(false);
      if (!quiet) toast.success("Draft saved.");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/cases/${caseId}/report`, { sections: report.sections, functional_matrix: report.functional_matrix, attestation: report.attestation });
      await api.post(`/cases/${caseId}/report/submit`);
      toast.success("Submitted to QA review.");
      const r = await api.get(`/cases/${caseId}/report`);
      setReport(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setSubmitting(false); }
  };

  return (
    <DashboardShell title="Report builder" subtitle={`${report.product?.name || ""} · Client: ${report.client?.name || ""} · Status: ${report.status}`}
      actions={<div className="flex gap-3">
        <Button variant="outline" data-testid="save-draft-btn" disabled={saving || locked} onClick={() => save(false)}
          className="rounded-full border-[#1E3F20]/30 text-[#1E3F20] hover:bg-[#E3DAC9]/40">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save draft"}
        </Button>
        <Button data-testid="submit-qa-btn" disabled={submitting || locked} onClick={submit}
          className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
          <Send className="h-4 w-4 mr-2" /> {locked ? "Locked — in QA" : submitting ? "Submitting…" : "Submit to QA"}
        </Button>
      </div>}>
      {report.status === "returned" && report.qa?.notes && (
        <div className="rounded-xl border border-[#8C2A2A]/30 bg-[#8C2A2A]/5 p-5 mb-6 max-w-4xl" data-testid="qa-return-notes">
          <p className="font-semibold text-[#8C2A2A] text-sm">Returned by QA — revision requested</p>
          <p className="text-sm mt-1 whitespace-pre-wrap">{report.qa.notes}</p>
        </div>
      )}
      <div className="max-w-4xl space-y-8">
        {!locked && (
          <section className="card-warm p-6" data-testid="voice-dictation-card">
            <h2 className="font-heading text-xl font-semibold">Voice dictation</h2>
            <p className="text-xs text-[#5C5C57] mt-1 mb-4">
              Speak naturally — the system transcribes, suggests the right report sections, and you approve every change.
              Audio is never stored; the raw transcript is kept in the audit trail. Nothing here creates a diagnosis — final interpretation stays with you.
            </p>
            <VoiceDictation caseId={caseId} mode="global" sectionLabels={report.section_labels}
              onApplied={(sections) => setReport((r) => ({ ...r, sections }))} />
          </section>
        )}
        <Accordion type="multiple" defaultValue={["referral_question", "results"]} className="space-y-3" data-testid="report-sections">
          {Object.entries(report.section_labels || {}).map(([k, label]) => (
            <AccordionItem key={k} value={k} className="card-warm px-6 border-[#E5E5DF]">
              <AccordionTrigger data-testid={`section-toggle-${k}`} className="font-heading text-lg font-semibold hover:no-underline">
                {label} {sections[k]?.trim() ? <span className="ml-2 text-xs text-[#2C5A2E] font-sans">●</span> : null}
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-xs text-[#5C5C57]">{PROMPTS[k]}</p>
                  {!locked && (
                    <VoiceDictation caseId={caseId} mode="field" targetField={k} fieldLabel={label}
                      sectionLabels={report.section_labels}
                      onApplied={(s) => setReport((r) => ({ ...r, sections: s }))} />
                  )}
                </div>
                <Textarea rows={5} data-testid={`section-input-${k}`} value={sections[k] || ""} disabled={locked}
                  onChange={(e) => setSection(k, e.target.value)} className="input-warm border-[#E5E5DF] text-sm leading-6" />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {caseDetail?.responses?.length > 0 && (
          <section className="card-warm p-6" data-testid="assessment-data-panel">
            <h2 className="font-heading text-xl font-semibold">Assessment response data</h2>
            <p className="text-xs text-[#5C5C57] mt-1 mb-4">Item-level responses collected from the client, beyond the summary scores. Use these to support the Results and Interpretation sections.</p>
            <Accordion type="multiple" className="space-y-2">
              {caseDetail.responses.map((r) => (
                <AccordionItem key={r.measure} value={r.measure} className="border border-[#E5E5DF] rounded-lg px-4">
                  <AccordionTrigger data-testid={`data-toggle-${r.measure}`} className="text-sm font-semibold hover:no-underline py-3">
                    {r.measure_name} — score {r.score}{r.max_score ? `/${r.max_score}` : ""} ({r.severity})
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pb-2">
                      {(r.detail || []).map((d, i) => (
                        <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-xs border-b border-[#E5E5DF] pb-2 last:border-0" data-testid={`data-item-${r.measure}-${i}`}>
                          <span className="sm:col-span-2 text-[#5C5C57]">{i + 1}. {d.question}</span>
                          <span className={`font-semibold ${d.value >= 2 ? "text-[#8C2A2A]" : "text-[#1C1C19]"}`}>{d.answer}</span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        <section className="card-warm p-6" data-testid="functional-matrix">
          <h2 className="font-heading text-xl font-semibold">IX. Functional Impact Matrix</h2>
          <p className="text-xs text-[#5C5C57] mt-1 mb-5">Every rating requires clinical evidence from the interview, measures, or records.</p>
          <div className="space-y-3">
            {matrix.map((m, i) => (
              <div key={m.domain} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center border-b border-[#E5E5DF] pb-3 last:border-0" data-testid={`matrix-row-${i}`}>
                <p className="text-sm font-semibold">{m.domain}</p>
                <Select value={m.rating === null || m.rating === undefined ? "" : String(m.rating)} disabled={locked}
                  onValueChange={(v) => setMatrix(i, "rating", parseInt(v, 10))}>
                  <SelectTrigger data-testid={`matrix-rating-${i}`} className="border-[#E5E5DF]"><SelectValue placeholder="Rating" /></SelectTrigger>
                  <SelectContent>
                    {RATINGS.map((r, vi) => <SelectItem key={r} value={String(vi)}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Clinical evidence" data-testid={`matrix-evidence-${i}`} value={m.evidence || ""} disabled={locked}
                  onChange={(e) => setMatrix(i, "evidence", e.target.value)} className="input-warm border-[#E5E5DF] md:col-span-2 text-sm" />
              </div>
            ))}
          </div>
        </section>

        <section className="card-warm p-6" data-testid="attestation-panel">
          <h2 className="font-heading text-xl font-semibold mb-2">XV. Clinician Attestation</h2>
          <p className="text-xs text-[#5C5C57] mb-4">I attest that I personally conducted this evaluation, interpreted the findings within my scope of licensure and competence, and that this report reflects my independent professional judgment.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input placeholder="Typed full name (signature)" data-testid="attestation-name" value={att.name || ""} disabled={locked}
              onChange={(e) => setAtt("name", e.target.value)} className="input-warm border-[#E5E5DF]" />
            <Input placeholder="Credentials (e.g., LCSW-C, Maryland)" data-testid="attestation-credentials" value={att.credentials || ""} disabled={locked}
              onChange={(e) => setAtt("credentials", e.target.value)} className="input-warm border-[#E5E5DF]" />
          </div>
          <label className="flex items-start gap-3 mt-4 text-sm cursor-pointer">
            <Checkbox data-testid="attestation-agree" checked={!!att.agreed} disabled={locked}
              onCheckedChange={(v) => setAtt("agreed", !!v)} className="mt-0.5" />
            I agree to the attestation above and authorize electronic signature of this report.
          </label>
        </section>
      </div>
    </DashboardShell>
  );
}
